const controllers = new WeakMap()

export function installProfileHistory (router) {
  if (controllers.has(router)) return
  const controller = { active: null }
  controllers.set(router, controller)
  // Register before Vue Router installs its window listeners. Capture alone
  // does not reliably give later window listeners priority in every browser.
  window.addEventListener('popstate', event => {
    if (controller.active) controller.active.onPopState(event)
  }, true)
  router.beforeEach((to, from, next) => {
    if (!controller.active || to.fullPath === from.fullPath) return next()
    // Clear overlays before Nuxt loads the next layout and destroys the card.
    controller.active.closeTo(0).then(() => next())
  })
}

// Profile overlays use same-URL entries, without navigating or scrolling.
export function createProfileHistory (router) {
  const controller = controllers.get(router)
  const marker = 'jatyProfileOverlay'
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  const url = window.location.href
  const layers = []
  const closingActions = []
  let pending = null
  let detached = false

  function currentDepth () {
    const state = window.history.state && window.history.state[marker]
    return window.location.href === url && state && state.id === id ? state.depth : 0
  }

  function detach () {
    if (detached) return
    detached = true
    if (controller.active === handlers) controller.active = null
  }

  function dismissTo (depth) {
    const dismissed = layers.splice(depth)
    // Run reactions while the preview still has its Vue event listeners.
    if (!depth) closingActions.splice(0).forEach(action => action())
    dismissed.reverse().forEach(close => close())
    if (!layers.length) detach()
  }

  function onPopState (event) {
    const samePage = window.location.href === url
    const depth = samePage ? currentDepth() : 0
    if (depth >= layers.length && !pending) return
    // Real navigation (for example history.go(-3)) must still reach the router.
    if (samePage) event.stopImmediatePropagation()
    const completed = pending
    pending = null
    dismissTo(depth)
    if (completed) completed.resolve()
  }

  function closeTo (depth) {
    if (pending) return pending.promise.then(() => closeTo(depth))
    if (layers.length <= depth) return Promise.resolve()
    const current = currentDepth()
    if (!current) {
      dismissTo(depth)
      return Promise.resolve()
    }
    let resolve
    const promise = new Promise(done => { resolve = done })
    pending = { promise, resolve }
    window.history.go(depth - current)
    return promise
  }

  const handlers = { onPopState, closeTo }
  controller.active = handlers

  return {
    push (close) {
      if (detached || pending) return false
      layers.push(close)
      window.history.pushState({
        ...window.history.state,
        [marker]: { id, depth: layers.length }
      }, '', url)
      return true
    },
    closeTop () { return closeTo(Math.max(0, layers.length - 1)) },
    closeAll (action) {
      if (action) {
        if (!layers.length) action()
        else closingActions.push(action)
      }
      return closeTo(0)
    },
    dispose () {
      // A card can also disappear through a store update. Keep its cleanup Back
      // event isolated from the router, without calling a destroyed component.
      closingActions.length = 0
      layers.fill(() => {})
      if (layers.length) closeTo(0)
      else detach()
    }
  }
}
